// Transfer lines: one per product, in one of its units, valued at the sending site's stock cost.
// A draft's cost comes from the API: the site's layers in FIFO order (the in-use one first), else the supplier.
import { type ProductUnit, factorOf, unitsOfProduct } from '../../components/purchasing/units';
import { parseNumber, priceText, qtyText } from '../Order/orderLineRules';

export interface TransferLine {
  /** The product id: the API keeps one line per product. */
  key: string;
  productId: string;
  name: string;
  sku: string;
  group: string;
  units: ProductUnit[];
  productUOMId: string;
  /** Per unit of the line: the API's cost, the saved one, or what the user typed where the API gives none. */
  cost: string;
  /** Where the cost comes from; null until the API has answered. */
  costSource: CostSource | null;
  /** Only a 0.00 cost that no stock layer gives is entered by hand. */
  costEditable: boolean;
  /** The costKey the API answered for; another key asks again. */
  costFor: string | null;
  /** Base units on hand at the sending site; null when not known. */
  stock: number | null;
  /** What is sent, as typed (drafts). */
  qty: string;
  /** What arrived, as typed (receiving). */
  received: string;
  sentQty: number;
  receivedQty: number;
  inventoryTransferId: string | null;
}

export type CostSource = 'stock' | 'supplier' | 'none';

export interface LineCost {
  cost: number;
  source: CostSource;
  editable: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const round4 = (value: number) => Math.round(value * 10000) / 10000;

export const unitOfLine = (line: Pick<TransferLine, 'units' | 'productUOMId'>): ProductUnit | undefined => line.units.find((unit) => unit.id === line.productUOMId);

export const unitName = (line: Pick<TransferLine, 'units' | 'productUOMId'>): string => unitOfLine(line)?.name ?? '';

/** Base units in one unit of the line. */
export const basePerUnit = (line: Pick<TransferLine, 'units' | 'productUOMId'>): number => {
  const factor = unitOfLine(line)?.factor ?? 1;
  const base = line.units.find((unit) => unit.isBase)?.factor ?? 1;
  return factor / base;
};

/** Cost of one unit of the line, as the transfer saves it; 0 while it is not known. */
export const costOf = (line: Pick<TransferLine, 'cost'>): number => {
  const n = parseNumber(line.cost);
  return n !== null && Number.isFinite(n) ? round2(n) : 0;
};

export const lineOfDetail = (detail: any): TransferLine => {
  const units = unitsOfProduct(detail?.product);
  const stock = Number(detail?.remainingQtySum);
  return {
    key: String(detail?.productId ?? ''),
    productId: String(detail?.productId ?? ''),
    name: detail?.productName ?? detail?.product?.productName ?? '',
    sku: detail?.productSku ?? detail?.product?.sku ?? '',
    group: detail?.product?.productGroup?.groupName ?? '',
    units,
    productUOMId: String(detail?.productUOMId ?? ''),
    cost: priceText(Number(detail?.cost) || 0),
    costSource: null,
    costEditable: false,
    costFor: null,
    stock: Number.isFinite(stock) ? stock : null,
    qty: qtyText(detail?.sentQty),
    received: '',
    sentQty: Number(detail?.sentQty) || 0,
    receivedQty: Number(detail?.receivedQty) || 0,
    inventoryTransferId: detail?.inventoryTransferId ?? null,
  };
};

/** A product picked from the sending site's list (`/products/listWithLocationId`); its cost comes from the API. */
export const lineOfPick = (product: any, productUOMId: string): TransferLine => ({
  key: String(product?.id ?? ''),
  productId: String(product?.id ?? ''),
  name: product?.productName ?? '',
  sku: product?.sku ?? '',
  group: product?.productGroup?.groupName ?? '',
  units: unitsOfProduct(product),
  productUOMId,
  cost: '',
  costSource: null,
  costEditable: false,
  costFor: null,
  stock: Number.isFinite(Number(product?.currentStock)) ? Number(product.currentStock) : null,
  qty: '',
  received: '',
  sentQty: 0,
  receivedQty: 0,
  inventoryTransferId: null,
});

const qtyOf = (line: Pick<TransferLine, 'qty'>): number => {
  const q = parseNumber(line.qty);
  return q !== null && Number.isFinite(q) ? Math.abs(q) : 0;
};

/** What the API is asked for this line at the sending site; a change of it asks again. */
export const costKey = (line: TransferLine, siteId: string): string => [siteId, line.productId, line.productUOMId, qtyOf(line)].join('|');

/** One line of POST /inventory-transfers/costs; an empty unit is left out. */
export const costRequest = (line: TransferLine) => ({
  productId: line.productId,
  ...(line.productUOMId ? { productUOMId: line.productUOMId } : {}),
  quantity: qtyOf(line),
});

/**
 * The API's answer for `askedFor`: a known cost replaces the line's; a cost to enter keeps what was typed or saved,
 * except one left by an earlier known cost, which starts again at 0.00.
 */
export const withLineCost = (line: TransferLine, known: LineCost, askedFor: string): TransferLine => {
  if (!known.editable) return { ...line, cost: priceText(known.cost), costSource: known.source, costEditable: false, costFor: askedFor };
  const fromKnown = line.costSource !== null && !line.costEditable;
  const keep = !fromKnown && line.cost.trim() !== '';
  return { ...line, cost: keep ? line.cost : priceText(0), costSource: known.source, costEditable: true, costFor: askedFor };
};

/** Another unit: a typed cost keeps its cost per base unit; a known one waits for the API's answer. */
export const withUnit = (line: TransferLine, productUOMId: string): TransferLine => {
  if (!line.costEditable) return { ...line, productUOMId, cost: '' };
  const n = parseNumber(line.cost);
  const cost = n !== null && Number.isFinite(n) ? priceText(round2((n / factorOf(line.units, line.productUOMId)) * factorOf(line.units, productUOMId))) : line.cost;
  return { ...line, productUOMId, cost };
};

export interface SentCheck {
  /** Blocks saving. */
  error: string;
  /** Only a cost to enter is checked; blocks saving. */
  costError: string;
  /** Amber, does not block. */
  notice: string;
  /** In the line's unit, for the notice. */
  onHand: number;
  qty: number;
  /** Null until the quantity and the cost are known. */
  value: number | null;
}

/** A draft line: something over 0 must go; more than is on hand is only noted. `flagged` shows empty fields. */
export const checkSent = (line: TransferLine, flagged: boolean): SentCheck => {
  const n = parseNumber(line.qty);
  const onHand = line.stock !== null ? round4(line.stock / basePerUnit(line)) : 0;
  const c = parseNumber(line.cost);
  const costError = !line.costEditable ? '' : c === null ? (flagged ? 'Enter a cost' : '') : Number.isNaN(c) ? 'Enter a number' : c < 0 ? "Can't be negative" : '';
  const base = { notice: '', onHand, qty: 0, value: null, costError };
  if (n === null) return { ...base, error: flagged ? 'Must be over 0' : '' };
  if (Number.isNaN(n)) return { ...base, error: 'Enter a number' };
  if (n <= 0) return { ...base, error: 'Must be over 0' };
  // The product line already says how much is on hand; the note under the qty stays short.
  const notice = line.stock !== null && n > onHand ? (onHand > 0 ? 'Exceeds stock' : 'None on hand') : '';
  const known = c !== null && Number.isFinite(c) && !costError;
  return { error: '', costError, notice, onHand, qty: n, value: known ? round2(costOf(line) * n) : null };
};

export interface ReceivedCheck {
  error: string;
  notice: string;
  /** The difference for the notice: short or over. */
  diff: number;
  counted: boolean;
  qty: number;
}

/** A line being received: what arrived must be entered (0 when nothing came); short and over are noted. */
export const checkReceived = (line: TransferLine, flagged: boolean): ReceivedCheck => {
  const n = parseNumber(line.received);
  const base = { notice: '', diff: 0, counted: false, qty: 0 };
  if (n === null) return { ...base, error: flagged ? 'Enter what arrived' : '' };
  if (Number.isNaN(n)) return { ...base, error: 'Enter a number' };
  if (n < 0) return { ...base, error: "Can't be negative" };
  const diff = round4(line.sentQty - n);
  return { error: '', notice: diff > 0 ? '{qty} short' : diff < 0 ? '{qty} more than sent' : '', diff: Math.abs(diff), counted: true, qty: n };
};

/** The lines as the transfer API takes them: edited (drafts), counted (receiving) or as saved (reject, cancel). */
export const linesPayload = (lines: TransferLine[], mode: 'edit' | 'receive' | 'keep') =>
  lines.map((line) => {
    const cost = costOf(line);
    const sentQty = mode === 'edit' ? parseNumber(line.qty) ?? 0 : line.sentQty;
    const receivedQty = mode === 'receive' ? parseNumber(line.received) ?? 0 : line.receivedQty;
    return {
      ...(line.inventoryTransferId ? { inventoryTransferId: line.inventoryTransferId } : {}),
      productId: line.productId,
      productSku: line.sku,
      productName: line.name,
      productUOMId: line.productUOMId,
      sentQty,
      receivedQty,
      varianceQty: round4(sentQty - receivedQty),
      cost,
      lineCost: round2(cost * sentQty),
    };
  });

/** Added, removed or edited lines of a draft, by product. */
export const draftChanges = (lines: TransferLine[], saved: TransferLine[]): { kind: 'added' | 'removed' | 'edited'; key: string }[] => {
  const before = new Map(saved.map((line) => [line.key, line]));
  const now = new Set(lines.map((line) => line.key));
  const out: { kind: 'added' | 'removed' | 'edited'; key: string }[] = [];
  lines.forEach((line) => {
    const old = before.get(line.key);
    if (!old) out.push({ kind: 'added', key: line.key });
    // A cost the API works out is not an edit; only a typed one is.
    else if (old.productUOMId !== line.productUOMId || parseNumber(old.qty) !== parseNumber(line.qty) || (line.costEditable && parseNumber(old.cost) !== parseNumber(line.cost)))
      out.push({ kind: 'edited', key: line.key });
  });
  saved.forEach((line) => {
    if (!now.has(line.key)) out.push({ kind: 'removed', key: line.key });
  });
  return out;
};
